/**
 * Removes properties with undefined values from an object.
 * Useful for cleaning up objects before sending them as query parameters.
 * @param obj The object to clean.
 * @returns A new object with undefined properties removed.
 */
export function cleanUndefined<T extends object>(obj: T): Partial<T> {
  const cleaned: Partial<T> = {};
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key) && obj[key] !== undefined) {
      cleaned[key] = obj[key];
    }
  }
  return cleaned;
}

/**
 * Parses overloaded arguments for AI client methods.
 * It distinguishes between (arg1, model, options) and (arg1, options) calling patterns.
 * @param defaultModel The default model to use if one is not provided.
 * @param modelOrOptions The second argument from the AI method, which can be a model string or an options object.
 * @param options The third argument from the AI method, which is the options object if a model string was provided.
 * @returns An object containing the resolved model string and the final options object.
 */
export function parseModelAndOptions<T extends object>(
  defaultModel: string,
  modelOrOptions?: string | T,
  options?: T
): { model: string; finalOptions: T } {
  let model: string;
  let finalOptions: T;

  if (typeof modelOrOptions === 'string') {
    model = modelOrOptions;
    finalOptions = options || ({} as T);
  } else {
    model = defaultModel;
    finalOptions = modelOrOptions || ({} as T);
  }

  return { model, finalOptions };
} 